import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import MenuIcon from "@mui/icons-material/Menu";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Skeleton,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { getApiUrl, getApiHeaders } from "../../config/api";

const MostCommonLostItems = ({ isTabView }) => {
  const [foundItems, setFoundItems] = useState([]);
  const [itemStats, setItemStats] = useState([]);
  const [chartItems, setChartItems] = useState([]);
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    const fetchFoundItems = async () => {
      try {
        setLoading(true);
        const response = await fetch(getApiUrl("found-item/list"), {
          headers: getApiHeaders()
        });
        if (!response.ok) throw new Error("Failed to fetch found items");
        const data = await response.json();
        const items = data.items || [];
        setFoundItems(items);

        // Group by item_type
        const typeCounts = {};
        items.forEach(item => {
          const type = item.item_type || "Unknown";
          if (!typeCounts[type]) typeCounts[type] = 0;
          typeCounts[type]++;
        });
        // Prepare stats for table
        const sortedTypes = Object.entries(typeCounts)
          .sort((a, b) => b[1] - a[1]);
        const stats = sortedTypes.map(([type, count], idx) => ({
          rank: idx + 1,
          itemType: type,
          totalLost: 0,
          totalReturned: count
        }));
        setItemStats(stats);

        // Prepare chart data
        const total = items.length;
        const chart = sortedTypes.map(([type, count]) => ({
          name: type,
          count: count,
          percentage: total ? ((count / total) * 100).toFixed(1) : 0
        }));
        setChartItems(chart);
      } catch (err) {
        setFoundItems([]);
        setItemStats([]);
        setChartItems([]);
      } finally {
        setLoading(false);
      }
    };
    fetchFoundItems();
  }, []);

  return (
    <Box sx={{ 
      height: "100%", 
      display: "flex", 
      gap: { xs: 1, sm: 2, md: 3 }, 
      flexDirection: { xs: "column", md: "row" },
      overflow: "hidden",
      width: "100%"
    }}>
      <TableContainer
        sx={{
          flex: { xs: "1 1 auto", md: "1 1 58%" },
          bgcolor: "#fbffe8",
          borderRadius: "15px",
          border: "2px solid #516d4f",
          minHeight: { xs: "300px", md: "auto" },
          minWidth: 0
        }}
      >
        <Table sx={{ tableLayout: "fixed", width: "100%" }}>
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={{ width: "15%", padding: { xs: "8px 4px", sm: "12px 8px", md: "16px" } }}>
                <Typography fontWeight="medium" color="#516d4f" variant="h6" sx={{ fontSize: { xs: "0.75rem", sm: "1rem", md: "1.25rem" } }}>Rank</Typography>
              </TableCell>
              <TableCell align="center" sx={{ width: "35%", padding: { xs: "8px 4px", sm: "12px 8px", md: "16px" } }}>
                <Typography fontWeight="medium" color="#516d4f" variant="h6" sx={{ fontSize: { xs: "0.75rem", sm: "1rem", md: "1.25rem" } }}>Item Type</Typography>
              </TableCell>
              <TableCell align="center" sx={{ width: "25%", padding: { xs: "8px 4px", sm: "12px 8px", md: "16px" } }}>
                <Typography fontWeight="medium" color="#516d4f" variant="h6" sx={{ fontSize: { xs: "0.75rem", sm: "1rem", md: "1.25rem" } }}>Total Lost Reports</Typography>
              </TableCell>
              <TableCell align="center" sx={{ width: "25%", padding: { xs: "8px 4px", sm: "12px 8px", md: "16px" } }}>
                <Typography fontWeight="medium" color="#516d4f" variant="h6" sx={{ fontSize: { xs: "0.75rem", sm: "1rem", md: "1.25rem" } }}>Total Returned</Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={index} hover>
                  <TableCell align="center" sx={{ padding: { xs: "8px 4px", sm: "12px 8px", md: "16px" } }}><Skeleton variant="text" width="40px" sx={{ mx: "auto" }} height={30} /></TableCell>
                  <TableCell align="center" sx={{ padding: { xs: "8px 4px", sm: "12px 8px", md: "16px" } }}><Skeleton variant="text" width="80%" sx={{ mx: "auto" }} height={30} /></TableCell>
                  <TableCell align="center" sx={{ padding: { xs: "8px 4px", sm: "12px 8px", md: "16px" } }}><Skeleton variant="text" width="80%" sx={{ mx: "auto" }} height={30} /></TableCell>
                  <TableCell align="center" sx={{ padding: { xs: "8px 4px", sm: "12px 8px", md: "16px" } }}><Skeleton variant="text" width="80%" sx={{ mx: "auto" }} height={30} /></TableCell>
                </TableRow>
              ))
            ) : (
              itemStats.map((item) => (
                <TableRow key={item.rank} hover>
                  <TableCell align="center" sx={{ padding: { xs: "8px 4px", sm: "12px 8px", md: "16px" } }}>
                    <Typography fontWeight="bold" color="#516d4f" variant="h6" sx={{ fontSize: { xs: "0.7rem", sm: "0.9rem", md: "1.1rem", lg: "1.25rem" } }}>{item.rank}</Typography>
                  </TableCell>
                  <TableCell align="center" sx={{ padding: { xs: "8px 4px", sm: "12px 8px", md: "16px" }, wordBreak: "break-word" }}>
                    <Typography color="#516d4f" variant="h6" sx={{ fontSize: { xs: "0.7rem", sm: "0.9rem", md: "1.1rem", lg: "1.25rem" } }}>{item.itemType}</Typography>
                  </TableCell>
                  <TableCell align="center" sx={{ padding: { xs: "8px 4px", sm: "12px 8px", md: "16px" } }}>
                    <Typography color="#516d4f" variant="h6" sx={{ fontSize: { xs: "0.7rem", sm: "0.9rem", md: "1.1rem", lg: "1.25rem" } }}>{item.totalLost}</Typography>
                  </TableCell>
                  <TableCell align="center" sx={{ padding: { xs: "8px 4px", sm: "12px 8px", md: "16px" } }}>
                    <Typography color="#516d4f" variant="h6" sx={{ fontSize: { xs: "0.7rem", sm: "0.9rem", md: "1.1rem", lg: "1.25rem" } }}>{item.totalReturned}</Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ 
        flex: { xs: "1 1 auto", md: "1 1 40%" }, 
        p: { xs: 2, sm: 3 }, 
        bgcolor: "#fbffe8", 
        borderRadius: "15px", 
        border: "2px solid #516d4f",
        minHeight: { xs: "300px", md: "auto" },
        minWidth: 0
      }}>
        <Typography 
          variant="h6" 
          fontWeight="semibold" 
          color="#516d4f" 
          sx={{ 
            mb: 2, 
            textAlign: "center",
            fontSize: { xs: "0.9rem", sm: "1.1rem", md: "1.25rem" }
          }}
        >
          Commonly Lost Items Chart
        </Typography>
        <Box sx={{ 
          width: { xs: 180, sm: 210, md: 240 }, 
          height: { xs: 180, sm: 220, md: 250 }, 
          mx: "auto", 
          mb: 2, 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "center" 
        }}>
          {chartItems.length > 0 ? (
            <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet">
              {(() => {
                const colors = ["#516d4f", "#8bb174", "#d0e7ba", "#e1ecc8", "#a5c38e"];
                let cumulativePercent = 0;
                return chartItems.map((item, idx) => {
                  const startAngle = (cumulativePercent / 100) * 360;
                  const percent = parseFloat(item.percentage);
                  const endAngle = ((cumulativePercent + percent) / 100) * 360;
                  cumulativePercent += percent;
                  
                  const startAngleRad = (startAngle - 90) * (Math.PI / 180);
                  const endAngleRad = (endAngle - 90) * (Math.PI / 180);
                  
                  const x1 = 50 + 40 * Math.cos(startAngleRad);
                  const y1 = 50 + 40 * Math.sin(startAngleRad);
                  const x2 = 50 + 40 * Math.cos(endAngleRad);
                  const y2 = 50 + 40 * Math.sin(endAngleRad);
                  
                  const largeArcFlag = percent > 50 ? 1 : 0;
                  
                  const pathData = [
                    `M 50 50`,
                    `L ${x1} ${y1}`,
                    `A 40 40 0 ${largeArcFlag} 1 ${x2} ${y2}`,
                    `Z`
                  ].join(' ');
                  
                  return (
                    <path
                      key={idx}
                      d={pathData}
                      fill={colors[idx % colors.length]}
                      stroke="#fff"
                      strokeWidth="0.5"
                    />
                  );
                });
              })()}
            </svg>
          ) : (
            <Typography color="#516d4f" sx={{ fontSize: { xs: "0.8rem", sm: "1rem" } }}>No data available</Typography>
          )}
        </Box>
        <Box sx={{ mt: 2 }}>
          {chartItems.map((item, idx) => (
            <Box key={idx} sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <Box sx={{ 
                width: { xs: 12, sm: 14, md: 16 }, 
                height: { xs: 12, sm: 14, md: 16 }, 
                bgcolor: ["#516d4f", "#8bb174", "#d0e7ba", "#e1ecc8", "#a5c38e"][idx % 5], 
                mr: 1, 
                borderRadius: "2px",
                flexShrink: 0
              }} />
              <Typography 
                variant="body2" 
                color="#516d4f"
                sx={{ 
                  fontSize: { xs: "0.65rem", sm: "0.8rem", md: "0.875rem" },
                  wordBreak: "break-word"
                }}
              >
                {item.name}: {item.percentage}% ({item.count})
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default MostCommonLostItems;